import { useMutation, useQuery } from '@redwoodjs/web';
import { useCallback } from 'react';
import { BillCommandInput, BillType } from 'types/graphql';
import './sales-order/bill.api.salesOrder';
import './purchase-order/bill.api.purchaseOrder';
import './grower-product/bill.api.growerProduct';
import './bso/bill.api.bso';
import { registerFragment } from '@redwoodjs/web/apollo';

registerFragment(gql`
  fragment BillShipmentFragment on Shipment {
    place {
      displayName
    }
    origin {
      displayName
    }
    destination {
      displayName
    }
    shipDate
    deliveryDate
    terms
    freightCompany {
      id
      name
    }
    temperatureMin
    temperatureMax
    customFields
  }
`);

registerFragment(gql`
  fragment BillDocumentFragment on Document {
    id
    title
    url
    type
    createdAt
    uploadedBy {
      user {
        id
        firstName
        lastName
      }
    }
  }
`);

export const GET_BILL = gql`
  query GET_BILL($slug: String!) {
    bill(slug: $slug) {
      id
      slug
      type
      status
      paymentTerms
      reference
      balancingAccountId
      customFields
      businessEntity {
        id
        name
        receivablesAccountId
        payablesAccountId
        places {
          id
          fullAddress
        }
        contacts {
          id
          name
        }
      }
      contact {
        id
        name
      }
      billingAddress {
        id
        fullAddress
      }
      openedAt
      invoiceDate
      dueDate
      paidPartialAt
      paidFullAt
      consignmentCommissionRate
      consignmentCommissionAmount
      isReconciled
      accountReconciliations {
        status
      }
      receipts {
        id
        slug
        voidedAt
        ledgerAccountPath
        paymentDate
        paymentAmount
        paymentMethod
        paymentDetails
        isReconciled
        accountReconciliations {
          status
        }
        parentPayment {
          slug
        }
      }
      adjustments {
        id
        amount
        memo
        reason
        balancingAccountPath
        isReconciled
      }
      lineItems {
        id
        billId
        description
        unitCount
        unitDescription
        unitPrice
        consignmentWeightUnitPrice
        consignmentWeightUnitPriceInCents
        proportionOfSale
        ledgerAccountId
        sourceExpenseId
        ledgerAccount {
          id
          accountName
          accountType
          accountNumber
        }
        sourceExpense {
          id
          expenseType {
            id
            name
          }
          description
          accountLevel
          # The charges grid renders the entity name. Without this the column came back undefined
          # on every lineItem-sourced charge and painted an empty, required-flagged cell over a
          # Business Entity the expense record actually had.
          businessEntity {
            id
            name
          }
          quantity
          unitAmount
          totalAmount
          settlementAmount
          chargePer
          includedInDlvPrice
          ledgerAccount {
            id
            accountName
            accountType
            accountNumber
          }
          lot {
            slug
            warehouseId
          }
          productionRun {
            slug
          }
          workOrder {
            slug
          }
        }
        sourceLineItemId
        sourceLineItem {
          id
          external
          settlementPriceWeight
          settlementPrice
          unitsPicked
          palletTagsOnLineItem {
            quantity
            palletTag {
              initialQuantity
              initialWeight
            }
          }
          sellPrice
          sellCurrencyPrice
          parentLineItem {
            sellPrice
            sellCurrencyPrice
          }
          salesOrder {
            id
            slug
            status
            flagged
            bill {
              status
            }
          }
          lot {
            id
            slug
            warehouseId
            initialQuantity
            initialWeight
            quantity
            weightPerInventoryUnit
            product {
              weightPerInventoryUnit
              variableWeight
            }
          }
        }
      }
      documents {
        ...BillDocumentFragment
      }
      notes {
        ...NoteAttributes
      }
      attachmentType
      salesOrder {
        ...BillSalesOrderFragment
      }
      purchaseOrder {
        ...BillPurchaseOrderFragment
      }
      buySellOrder {
        ...BillBSOFragment
      }
      growerProduct {
        ...BillGrowerProductFragment
      }
      qboLink {
        thirdPartyId
        syncError
        syncErrorSolution
        syncedAt
        syncStartedAt
        triggeredByEmail
        updatedAt
      }
    }
  }
`;

export const useRepublishToQbo = () => {
  const [republishToQbo] = useMutation(gql`
    mutation REPUBLISH_TO_QBO($slug: String!) {
      republishToQbo(slug: $slug)
    }
  `);

  return async (slug: string) => {
    const response = await republishToQbo({ variables: { slug } });
    return response.data?.republishToQbo ?? 'QuickBooks sync requested';
  };
};

export const useBulkRepublishToQbo = () => {
  const [bulkRepublishToQbo] = useMutation(gql`
    mutation BULK_REPUBLISH_TO_QBO($slugs: [String!]!) {
      bulkRepublishToQbo(slugs: $slugs)
    }
  `);

  return async (slugs: string[]) => {
    const response = await bulkRepublishToQbo({ variables: { slugs } });
    return response.data?.bulkRepublishToQbo;
  };
};

export const useApplyBillCommands = () => {
  const [applyCommands] = useMutation(gql`
    mutation APPLY_BILL_COMMANDS($commands: [BillCommandInput!]!) {
      applyBillCommands(commands: $commands) {
        id
        slug
        status
        adjustments {
          id
        }
      }
    }
  `);

  const applyFn = useCallback(
    async (commands: BillCommandInput[]) => {
      const res = await applyCommands({ variables: { commands } });
      return res.data?.applyBillCommands;
    },
    [applyCommands]
  );

  return applyFn;
};

export const useDeleteBill = () => {
  const [deleteBill] = useMutation(gql`
    mutation DELETE_BILL($billId: Int!) {
      deleteBill(id: $billId)
    }
  `);

  return async (billId: number) => {
    return await deleteBill({ variables: { billId } });
  };
};

export const useCreateBill = () => {
  const applyCommands = useApplyBillCommands();

  return async (billType: BillType) => {
    return await applyCommands([{ createBill: { billType } }]);
  };
};

export const useBillSuggestedValues = (
  businessEntityId: number | undefined,
  type: BillType,
  skip = false
): { contactId: number; billingAddressId: number; paymentTerms: string; balancingAccountId: number } | undefined => {
  const { data } = useQuery(
    gql`
      query GET_BILL_SUGGESTED_VALUES($businessEntityId: Int!, $type: BillType!) {
        billSuggestedValues(businessEntityId: $businessEntityId, type: $type) {
          contactId
          billingAddressId
          paymentTerms
          balancingAccountId
        }
      }
    `,
    // `$businessEntityId` is `Int!`; skip until an entity exists so GraphQL doesn't reject the operation for a missing required var.
    { variables: { businessEntityId, type }, skip: skip || businessEntityId == null }
  );

  return data?.billSuggestedValues;
};
